import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Layers, ListChecks, Info } from 'lucide-react';
import { STAGE_LABELS, summarizeInsuranceSales } from '@/utils/insuranceSales';

/**
 * Analytics: Faye travel insurance sales (founder, 2026-10-23 — "I have no
 * idea if people are purchasing this"). How many plans sold in the period, the
 * premiums, how often checkout converts, the plan mix, every policy bought,
 * and the policies ops still buys at ticketing. Presentational: the page
 * fetches /api/admin/insurance-summary and passes its own period down.
 */

const usd = (cents) => `$${((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd0 = (cents) => `$${Math.round((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(n < 0.1 ? 1 : 0)}%`);
const shortDate = (iso, withYear = false) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '—';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
};
/** A purchase instant shown on the viewer's own calendar, like the rest of the page. */
const localDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const STAGE_TONE = {
  buy_now: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  at_ticketing: 'bg-white/[0.05] text-white/70 border-white/10',
  settling: 'bg-amber-500/10 text-amber-200/80 border-amber-400/20',
  issued: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/25',
  cancel_with_faye: 'bg-amber-500/10 text-amber-200/80 border-amber-400/20',
  refund_due: 'bg-amber-500/10 text-amber-200/80 border-amber-400/20',
  refunded: 'bg-white/[0.04] text-white/50 border-white/10',
  cancelled: 'bg-white/[0.04] text-white/50 border-white/10',
  unknown: 'bg-white/[0.04] text-white/50 border-white/10',
};

export default function TravelInsuranceSales({ policies, bookings, startDate, endDate, loading, error }) {
  const s = useMemo(
    () => summarizeInsuranceSales({ policies: policies || [], bookings: bookings || [], startDate, endDate }),
    [policies, bookings, startDate, endDate],
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 mb-12" aria-busy="true">
        <div className="h-5 w-40 bg-white/10 rounded animate-pulse mb-6" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-12 text-white/60 text-sm font-normal flex gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Travel insurance sales couldn&apos;t load: {error}</span>
      </div>
    );
  }
  if (!policies) return null;

  const take = s.takeRate;
  const tiles = [
    {
      label: 'Policies sold',
      value: String(s.sold),
      sub: s.sold ? `${s.atCheckout} at checkout · ${s.addedLater} added later` : 'none in this period',
    },
    {
      label: 'Premiums',
      value: usd0(s.premiumCents),
      sub: 'collected for Faye',
    },
    {
      label: 'Checkout take rate',
      value: take.beforeLaunch ? '—' : pct(take.rate),
      sub: take.beforeLaunch
        ? 'Faye launched Oct 3, 2026'
        : take.eligible ? `${take.bought} of ${take.eligible} eligible trips` : 'no eligible trips booked',
    },
    {
      label: 'Still to buy',
      value: String(s.queue.count),
      sub: s.queue.buyNow
        ? `${s.queue.buyNow} ticketed — buy now`
        : s.queue.count ? `at ticketing · first departs ${shortDate(s.queue.nextDeparture)}` : 'nothing waiting',
      alert: s.queue.buyNow > 0,
    },
  ];
  const maxPlan = Math.max(1, ...s.plans.map((p) => p.count));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="mb-12"
      data-testid="travel-insurance-sales"
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-white/40" />
        <h2 className="text-white font-light text-xl tracking-tight">Travel insurance</h2>
      </div>
      <p className="text-white/45 text-[13px] font-normal mb-5 max-w-2xl leading-snug">
        Faye plans customers bought in this period, how they bought them, and the policies we
        still buy from Faye when each trip is ticketed.
      </p>

      {/* KPI row — 2-up on phones, 4-up from sm. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-normal mb-1.5">{t.label}</p>
            <p className="text-white text-2xl font-light tracking-tight leading-none mb-1.5">{t.value}</p>
            <p className={`text-[11px] font-normal leading-tight ${t.alert ? 'text-amber-300' : 'text-white/35'}`}>{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── Plan mix ───────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-3.5 h-3.5 text-white/40" />
            <h3 className="text-white text-sm font-medium">Plans sold</h3>
          </div>
          {s.plans.length === 0 ? (
            <p className="text-white/40 text-[13px] font-normal">No plans sold in this period.</p>
          ) : (
            <ul className="space-y-3">
              {s.plans.map((p) => (
                <li key={p.label}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="text-white/80 text-[13px] font-normal truncate">{p.label}</span>
                    <span className="text-white/50 text-[12px] font-normal tabular-nums whitespace-nowrap">
                      {p.count} · {usd0(p.premiumCents)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-400/70" style={{ width: `${(p.count / maxPlan) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-5 pt-3 border-t border-white/[0.06] text-white/40 text-[11px] font-normal leading-snug flex gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            <span>
              Take rate: Defera Capital round trips with a base fare of $800 or more, booked since
              launch. A few of those couldn&apos;t have bought it for other reasons, so the rate errs low.
            </span>
          </p>
        </div>

        {/* ── Every policy bought in the period ─────────────────────── */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="w-3.5 h-3.5 text-white/40" />
            <h3 className="text-white text-sm font-medium">Policies</h3>
            <span className="ml-auto text-white/35 text-[11px] font-normal">{s.allTimeSold} all time</span>
          </div>
          {s.rows.length === 0 ? (
            <p className="text-white/40 text-[13px] font-normal">No insurance bought in this period.</p>
          ) : (
            <ul className="divide-y divide-white/[0.06]">
              {s.rows.map((p) => (
                <li key={p.id} className="py-2.5 grid grid-cols-[1fr_auto] sm:grid-cols-[5.5rem_1fr_auto_auto] gap-x-3 gap-y-1 items-center">
                  <span className="font-mono text-[13px] text-white/85">
                    {p.booking_id ? (
                      <Link to={`/trips?tripId=${encodeURIComponent(p.booking_id)}`} className="hover:text-white underline-offset-2 hover:underline">
                        {p.trip_reference || '—'}
                      </Link>
                    ) : (p.trip_reference || '—')}
                  </span>
                  <span className="text-white text-[13px] font-normal tabular-nums text-right sm:order-3">{usd(p.premium_cents)}</span>
                  <span className="text-white/55 text-[12px] font-normal leading-snug min-w-0 sm:truncate sm:order-2">
                    {p.plan_label || 'Faye'} · {p.how === 'checkout' ? 'checkout' : 'added later'} {localDate(p.created_at)} · departs {shortDate(p.depart_date, true)}
                  </span>
                  <span className={`justify-self-end sm:order-4 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-normal whitespace-nowrap ${STAGE_TONE[p.stage] || STAGE_TONE.unknown}`}>
                    {STAGE_LABELS[p.stage] || STAGE_LABELS.unknown}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
}

TravelInsuranceSales.propTypes = {
  /** From /api/admin/insurance-summary; null until loaded. */
  policies: PropTypes.arrayOf(PropTypes.object),
  /** The page's raw booking rows (premiums intact). */
  bookings: PropTypes.arrayOf(PropTypes.object),
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  loading: PropTypes.bool,
  error: PropTypes.string,
};
